<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../../framework/js/api.js';
  import {
    AppEmptyState,
    AppLoadingState,
    AppErrorState,
    AppToolbar,
    SortControl,
    StatusPills,
    AssessmentTile,
    AssessmentPager,
    relativeTime,
    matchesSearch,
    paginate,
  } from '../../framework/components/app';
  import type { RingSpec, RingTone, StatusTone, PillOption } from '../../framework/components/app';

  interface GapfinderRow {
    uuid: string;
    title: string | null;
    overall_score: number | null;
    overall_status: string | null;
    generation_status: string;
    source_type: string | null;
    source_file_name?: string | null;
    updated_at: string;
    created_at: string;
  }

  let items: GapfinderRow[] = [];
  let state: 'loading' | 'ready' | 'error' = 'loading';
  let searchQuery = '';
  let statusFilter: string[] = [];
  let sortField: 'newest' | 'oldest' | 'titleAsc' | 'titleDesc' = 'newest';
  let page = 1;
  let pageSize = 20;
  let searchTimeout: ReturnType<typeof setTimeout>;

  const STATUS_OPTIONS: PillOption[] = [
    { value: 'decided', label: 'Decided' },
    { value: 'draft', label: 'Draft' },
  ];

  /** Aligned with tile when analysis has a score (or completed). */
  function isDecidedRow(row: GapfinderRow): boolean {
    if (row.overall_score !== null && row.overall_score !== undefined) return true;
    return (row.generation_status || '').toLowerCase() === 'completed';
  }

  /** Aligned with tile “Draft” — excludes in-flight and failed. */
  function isDraftRow(row: GapfinderRow): boolean {
    if (isDecidedRow(row)) return false;
    const gs = (row.generation_status || '').toLowerCase();
    return !['processing', 'queued', 'failed'].includes(gs);
  }

  const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'titleAsc', label: 'A – Z' },
    { value: 'titleDesc', label: 'Z – A' },
  ];

  async function loadData() {
    state = 'loading';
    try {
      const res: any = await api.getGapfinderAnalyses();
      if (res?.success && res?.data?.list) {
        items = res.data.list;
        state = 'ready';
      } else {
        state = 'error';
      }
    } catch { state = 'error'; }
  }

  function onSearch(e: CustomEvent<string>) {
    if (searchTimeout) clearTimeout(searchTimeout);
    searchQuery = e.detail;
    page = 1;
    if (searchQuery.length === 0 || searchQuery.length >= 3) {
      searchTimeout = setTimeout(() => { searchQuery = searchQuery; }, 200);
    }
  }

  function onStatusChange(e: CustomEvent<string[]>) {
    statusFilter = e.detail;
    page = 1;
  }

  function onSort(e: CustomEvent<{ field: string }>) {
    sortField = e.detail.field as typeof sortField;
    page = 1;
  }

  function scoreTone(score: number | null): RingTone {
    if (score == null) return 'neutral';
    if (score >= 85) return 'success';
    if (score >= 55) return 'warn';
    return 'danger';
  }

  function scoreBadgeTone(score: number | null): StatusTone {
    if (score == null) return 'neutral';
    if (score >= 85) return 'success';
    if (score >= 55) return 'warn';
    return 'danger';
  }

  function tileFor(row: GapfinderRow): {
    ring: RingSpec;
    ringTone: RingTone;
    statusLabel: string;
    statusTone: StatusTone;
    statusDetail: string;
    subtitle: string;
    footerNote: string;
  } {
    const gs = (row.generation_status || '').toLowerCase();
    const sourceLabel = row.source_file_name
      || (row.source_type ? row.source_type.replace(/_/g, ' ') : '');

    if (row.overall_score !== null && row.overall_score !== undefined) {
      return {
        ring: { kind: 'percent', value: row.overall_score },
        ringTone: scoreTone(row.overall_score),
        statusLabel: row.overall_status || 'Complete',
        statusTone: scoreBadgeTone(row.overall_score),
        statusDetail: '',
        subtitle: sourceLabel,
        footerNote: `Generated ${relativeTime(row.updated_at || row.created_at)}`,
      };
    }

    if (gs === 'processing') {
      return {
        ring: { kind: 'dots' },
        ringTone: 'progress',
        statusLabel: 'Analyzing',
        statusTone: 'info',
        statusDetail: '',
        subtitle: sourceLabel,
        footerNote: `Started ${relativeTime(row.updated_at || row.created_at)}`,
      };
    }
    if (gs === 'queued') {
      return {
        ring: { kind: 'dots' },
        ringTone: 'neutral',
        statusLabel: 'Queued',
        statusTone: 'neutral',
        statusDetail: '',
        subtitle: sourceLabel,
        footerNote: `Submitted ${relativeTime(row.updated_at || row.created_at)}`,
      };
    }
    if (gs === 'failed') {
      return {
        ring: { kind: 'icon', name: 'exclamation' },
        ringTone: 'danger',
        statusLabel: 'Failed',
        statusTone: 'danger',
        statusDetail: 'Retry available',
        subtitle: sourceLabel,
        footerNote: `Failed ${relativeTime(row.updated_at || row.created_at)}`,
      };
    }
    return {
      ring: { kind: 'icon', name: 'pencil' },
      ringTone: 'neutral',
      statusLabel: 'Draft',
      statusTone: 'neutral',
      statusDetail: '',
      subtitle: sourceLabel,
      footerNote: `Edited ${relativeTime(row.updated_at || row.created_at)}`,
    };
  }

  function compareItems(a: GapfinderRow, b: GapfinderRow, field: typeof sortField): number {
    if (field === 'newest') {
      return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime();
    }
    if (field === 'oldest') {
      return new Date(a.updated_at || a.created_at).getTime() - new Date(b.updated_at || b.created_at).getTime();
    }
    if (field === 'titleDesc') {
      return (b.title || '').localeCompare(a.title || '');
    }
    return (a.title || '').localeCompare(b.title || '');
  }

  $: filtered = items.filter((row) => {
    if (statusFilter.length > 0) {
      const f = statusFilter[0];
      if (f === 'decided' && !isDecidedRow(row)) return false;
      if (f === 'draft' && !isDraftRow(row)) return false;
    }
    if (searchQuery.length >= 3) {
      return matchesSearch(row.title || '', searchQuery);
    }
    return true;
  });

  $: sorted = [...filtered].sort((a, b) => compareItems(a, b, sortField));
  $: totalCount = sorted.length;
  $: pagedItems = paginate(sorted, page, pageSize);

  $: statusCounts = STATUS_OPTIONS.map((o) => ({
    ...o,
    count: items.filter((r) => (o.value === 'decided' ? isDecidedRow(r) : isDraftRow(r))).length,
  }));

  onMount(loadData);
</script>

{#if state === 'loading'}
  <AppLoadingState label="Loading analyses" />
{:else if state === 'error'}
  <AppErrorState on:retry={loadData} />
{:else if items.length === 0}
  <AppEmptyState
    icon="shield alternate"
    title="No analyses yet"
    description="Start a new analysis to see results here."
    actionLabel="New analysis"
    actionHref="/gapfinder"
  />
{:else}
  <div class="filter-bar">
    <AppToolbar
      bind:searchValue={searchQuery}
      searchPlaceholder="Search by title…"
      searchLabel="Search analyses"
      resultsCount={totalCount}
      resultsSingular="analysis"
      resultsPlural="analyses"
      on:search={onSearch}
    >
      <SortControl
        slot="controls"
        options={SORT_OPTIONS}
        field={sortField}
        on:change={onSort}
      />
    </AppToolbar>
    <StatusPills
      options={statusCounts}
      selected={statusFilter}
      on:change={onStatusChange}
    />
  </div>

  {#if pagedItems.length === 0}
    <div class="empty-filter">
      <p>No analyses match your filters.</p>
      <button type="button" class="reset-btn" on:click={() => { searchQuery = ''; statusFilter = []; page = 1; }}>
        Clear filters
      </button>
    </div>
  {:else}
    <div class="tile-grid">
      {#each pagedItems as row (row.uuid)}
        {@const t = tileFor(row)}
        <AssessmentTile
          agent="gapfinder"
          href={`/gapfinder/${row.uuid}`}
          title={row.title || 'Untitled analysis'}
          subtitle={t.subtitle}
          ring={t.ring}
          ringTone={t.ringTone}
          statusLabel={t.statusLabel}
          statusTone={t.statusTone}
          statusDetail={t.statusDetail}
          footerNote={t.footerNote}
        />
      {/each}
    </div>
  {/if}

  <AssessmentPager
    bind:page
    bind:pageSize
    totalCount={totalCount}
  />
{/if}

<style>
  .filter-bar {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    margin-bottom: var(--space-5);
  }

  .tile-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--space-6);
  }

  @media (max-width: 1440px) {
    .tile-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  }

  @media (max-width: 1024px) {
    .tile-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }

  @media (max-width: 640px) {
    .tile-grid { grid-template-columns: 1fr; }
  }

  .empty-filter {
    text-align: center;
    padding: var(--space-8) var(--space-4);
    color: var(--grey-secondary);
  }

  .empty-filter p { margin: 0 0 var(--space-3) 0; }

  .reset-btn {
    background: transparent;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-input);
    color: var(--primary);
    font: inherit;
    font-size: var(--text-sm);
    padding: var(--space-2) var(--space-4);
    cursor: pointer;
  }

  .reset-btn:hover { background: var(--surface-hover); border-color: var(--primary); }
</style>
